import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { Result, Slot, Calendar, SlotConflict, ScriptsWithObjections } from '../dto/model';
import { cleanupExpiredOppInLocalStorage, TTL_MINUTES } from './utils';
import { QualifyingAnswer } from '../utils/lead-queue-utils';
import { FlowQuestion } from '../dto/qualifying-flow-models';
import { fromBackendOptions, fromBackendRules } from './admin-page-service';

dayjs.extend(customParseFormat);

const rootUrl = (process as any).env.REACT_APP_API_ROOT_URL;

const handleResponse = async (url: string, options: RequestInit): Promise<Response> => {
  const response = await fetch(url, options);

  if (!response.ok) {
    throw {
      status: response.status,
      statusText: response.statusText,
    };
  }

  return response;
};

export const salesAppointmentsService = {
  async getLatestSlot(accessToken: string, opportunityId: string, salesChannel: string): Promise<Result<Slot>> {
    const url = `${rootUrl}/api/v1/slots?opportunityId=${opportunityId}&salesChannel=${salesChannel}`;
    const requestOptions = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    };
    const response = await fetch(url, requestOptions);
    const responseJson = await response.json();
    return responseJson;
  },

  async getAvailableSlots(
    accessToken: string,
    calendarWeek: string,
    opportunityId: string,
    signupId: string,
    salesChannel?: string,
    sourceSystemHeader?: string,
    salesManagerId?: string
  ): Promise<Calendar> {
    //Do some cleaning when local storage gets too big
    if (window.localStorage.length > 12) {
      cleanupExpiredOppInLocalStorage();
    }
    const uniqueVisitObj = JSON.parse(window.localStorage.getItem(opportunityId) || '{}');
    const currentTime = Date.now();

    // Check if 15 minutes has passed since the uniqueVisit was set
    if (uniqueVisitObj.time && currentTime - uniqueVisitObj.time > TTL_MINUTES * 60 * 1000) {
      window.localStorage.removeItem(opportunityId);
      uniqueVisitObj.time = null;
    }
    const baseUrl = `${rootUrl}/api/v1/calendar/${calendarWeek}`;
    const salesManagerPath = `/sales-manager/${salesManagerId}`;
    const bookingPath = opportunityId ? `?opportunityId=${opportunityId}` : `?signupId=${signupId}&salesChannel=${salesChannel}`;

    const url = `${baseUrl}${salesManagerId ? salesManagerPath : bookingPath}`;

    const headersToUse: { [key: string]: string } = {
      UniqueVisit: !uniqueVisitObj.time && opportunityId ? 'true' : 'false',
      Authorization: `Bearer ${accessToken}`,
    };
    if (sourceSystemHeader) {
      headersToUse['sourceui'] = sourceSystemHeader;
    }

    const response = await handleResponse(url, {
      method: 'GET',
      headers: headersToUse,
    });
    const calendar = (await response.json()).data;

    //Set the unique visit time as all other visits will not be unique
    if (!uniqueVisitObj.time) {
      uniqueVisitObj.time = currentTime;
      window.localStorage.setItem(opportunityId, JSON.stringify(uniqueVisitObj));
    }

    const res: Calendar = {
      ...calendar,
      slots: calendar.slots.map((x: any) => ({
        ...x,
        startDate: dayjs(x.startDate),
        endDate: dayjs(x.endDate),
      })),
    };
    res.slots = res.slots.filter((s) => s.startDate.isoWeekday() !== 7);

    return res;
  },

  async bookSlot(opportunityId: string, signupId: string, slotDate: Dayjs, accessToken: string, sourceSystemHeader: string, salesChannel?: string, bookingTypeHeader?: string): Promise<Result<Slot>> {
    const url = `${rootUrl}/api/v2/slot`;

    const headersToUse: { [key: string]: string } = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    };
    if (sourceSystemHeader) {
      headersToUse['sourceui'] = sourceSystemHeader;
    }
    if (bookingTypeHeader) {
      headersToUse['booking-type'] = bookingTypeHeader;
    }

    const data: Record<string, string> = {
      slotDate: slotDate.utc().format(),
    };

    if (opportunityId) {
      data['opportunityId'] = opportunityId;
    } else if (signupId && salesChannel) {
      data['signupId'] = signupId;
      data['salesChannel'] = salesChannel;
    }

    const requestOptions = {
      method: 'PUT',
      headers: headersToUse,
      body: JSON.stringify(data),
    };
    const response = await fetch(url, requestOptions);
    const responseJson = await response.json();

    if (responseJson.isSuccess) {
      window.localStorage.removeItem(opportunityId);
    }

    return responseJson;
  },

  async getSlotConflicts(startDate: Dayjs, endDate: Dayjs, salesChannel: string, accessToken: string): Promise<Result<SlotConflict[]>> {
    const url = `${rootUrl}/api/v1/slot/conflicts/${startDate.format('YYYY-MM-DD')}/${endDate.format('YYYY-MM-DD')}?salesChannel=${salesChannel}`;

    const requestOptions = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    };

    const response = await handleResponse(url, requestOptions);
    return await response.json();
  },

  async rebookSlot(slotId: number, salesChannel: string, accessToken: string, oppId: string): Promise<Result<string>> {
    const url = `${rootUrl}/api/v1/slots/${slotId}/rebook?opportunityId=${oppId}&salesChannel=${salesChannel}`;
    const requestOptions = {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    };
    const response = await fetch(url, requestOptions);
    if (response.status === 403) {
      throw {
        status: 403,
        statusText: 'Forbidden',
      };
    }
    return await response.json();
  },

  async getScripts(accessToken: string): Promise<Result<ScriptsWithObjections[]>> {
    const url = `${rootUrl}/api/v1/scripts`;
    const requestOptions = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    };

    const response = await fetch(url, requestOptions);
    if (response.status === 403) {
      throw {
        status: 403,
        statusText: 'Forbidden',
      };
    }
    return await response.json();
  },

  async postScripts(scripts: ScriptsWithObjections[], accessToken: string): Promise<void> {
    const url = `${rootUrl}/api/v1/scripts`;
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(scripts),
    };

    const response = await fetch(url, requestOptions);
    if (!response.ok) {
      throw {
        status: response.status,
        statusText: response.statusText,
      };
    }
  },

  async getActiveQualificationFlow(accessToken: string, salesChannel: string): Promise<Result<FlowQuestion[]>> {
    const url = `${rootUrl}/api/v1/qualification-flows/active?salesChannel=${salesChannel}`;
    const requestOptions = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    };
    try {
      const response = await fetch(url, requestOptions);
      const raw = (await response.json()) as Result<any>;
      if (!raw.isSuccess) return { data: [], errors: raw.errors, isSuccess: false };

      const flow = raw.data as any;
      const questions: FlowQuestion[] = (flow.questions ?? []).map((slot: any) => {
        const q = slot.question ?? slot;
        return {
          feId: crypto.randomUUID(),
          question: {
            id: q.id,
            feId: crypto.randomUUID(),
            questionKey: q.questionKey,
            questionText: q.questionText ?? {},
            questionType: q.questionType,
            options: fromBackendOptions(q.questionType, q.options),
            allowEmpty: q.questionType === 'NumberField' ? (q.options?.AllowEmpty ?? q.options?.allowEmpty ?? false) : undefined,
            sortOrder: q.sortOrder,
            isActive: q.isActive ?? true,
          },
          rules: fromBackendRules(slot.rules ?? []),
        } as FlowQuestion;
      });

      return { data: questions, errors: [], isSuccess: true };
    } catch (error) {
      return { data: [], errors: [`${JSON.stringify(error)}`], isSuccess: false };
    }
  },

  async saveQualifyingAnswers(accessToken: string, opportunityId: string, signupId: string, qualifyingAnswers: QualifyingAnswer[]): Promise<Result<void>> {
    const url = `${rootUrl}/api/v1/qualifying-answers`;
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        opportunityId,
        signupId,
        qualifyingAnswers,
      }),
    };
    const response = await fetch(url, requestOptions);
    return await response.json();
  },
};
